import { useCallback, useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'

export type FaqCategory = 'Candidats' | 'Employeurs' | 'Formation' | 'BNSSA' | 'MNS'

export interface FaqItem {
  _id: string
  question: string
  answer: string
  category: FaqCategory
  order: number
  isActive?: boolean
  creator?: string
  createdAt: string
  updatedAt: string
}

export interface NewFaqItem {
  question: string
  answer: string
  category: FaqCategory
  order: number
  isActive?: boolean
}

export function useFaqs() {
  const [items, setItems] = useState<FaqItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await lumi.entities.faq_items.list({
        sort: { category: 1, order: 1 },
      })
      const list = (res.list as FaqItem[]) || []
      setItems(list)
      console.log('[useFaqs] fetched:', list.length)
    } catch (e) {
      const msg = (e as Error)?.message || 'Erreur de chargement FAQ'
      console.error('[useFaqs] fetch failed:', e)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const createFaq = useCallback(
    async (data: NewFaqItem, creator?: string) => {
      const now = new Date().toISOString()
      const doc = {
        ...data,
        isActive: data.isActive ?? true,
        creator: creator ?? 'admin',
        createdAt: now,
        updatedAt: now,
      }
      const created = await lumi.entities.faq_items.create(doc)
      console.log('[useFaqs] created:', created._id)
      await fetchAll()
      return created
    },
    [fetchAll],
  )

  const updateFaq = useCallback(
    async (id: string, patch: Partial<NewFaqItem>) => {
      const now = new Date().toISOString()
      const updated = await lumi.entities.faq_items.update(id, {
        ...patch,
        updatedAt: now,
      })
      console.log('[useFaqs] updated:', id)
      await fetchAll()
      return updated
    },
    [fetchAll],
  )

  const deleteFaq = useCallback(
    async (id: string) => {
      await lumi.entities.faq_items.delete(id)
      console.log('[useFaqs] deleted:', id)
      await fetchAll()
    },
    [fetchAll],
  )

  const moveFaq = useCallback(
    async (id: string, direction: 'up' | 'down') => {
      const item = items.find((i) => i._id === id)
      if (!item) return
      const sameCategory = items
        .filter((i) => i.category === item.category)
        .sort((a, b) => a.order - b.order)
      const idx = sameCategory.findIndex((i) => i._id === id)
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1
      if (targetIdx < 0 || targetIdx >= sameCategory.length) return
      const target = sameCategory[targetIdx]
      const now = new Date().toISOString()
      // Swap orders
      await Promise.all([
        lumi.entities.faq_items.update(item._id, { order: target.order, updatedAt: now }),
        lumi.entities.faq_items.update(target._id, { order: item.order, updatedAt: now }),
      ])
      console.log('[useFaqs] moved:', id, direction)
      await fetchAll()
    },
    [items, fetchAll],
  )

  return { items, loading, error, fetchAll, createFaq, updateFaq, deleteFaq, moveFaq }
}
